/**
 * contracts.md §9 — the entity/key map, the parent's §9 table as code.
 *
 * Every key in the system is built by one of these functions. No handler concatenates a
 * key string, because a hand-built key is how a subtree becomes unreachable — silently,
 * and only for the rows written after the typo. (CI check 7.)
 *
 * **This module is also the one sanctioned place a brand is applied** (CI check 6: neither
 * `GateId` nor `BrokerCode` is cast away outside the key-builder module). The `as`
 * expressions below are the only ones in the package; every other module receives already-
 * branded values.
 *
 * Three properties this module keeps true (contracts.md §9):
 *   1. Keys use `GateId`, never `BrokerCode`.
 *   2. No null placeholders (D46) — absence is absence. Item types use optional
 *      properties, never nullable ones, so `attribute_not_exists` keeps meaning what it says.
 *   3. This is the single place a tenancy layer would ever be added. A parent ORG arrives
 *      as a new `ORG#id` entity plus an `orgId` attribute — additive, no re-key, no
 *      backfill. **If a change here would force existing items to move, it is the wrong change.**
 */
/** PK/SK pairs. Both are opaque to callers; only these builders construct them. */
export const keys = {
    gateMeta: (id) => ({ PK: `GATE#${id}`, SK: 'META' }),
    /**
     * contracts.md §1 — **the broker claim**: the printed controller code → the generated
     * `GateId` it was claimed as.
     *
     * This is the one place a `BrokerCode` appears in a key, and it is not a violation of
     * *"nothing keys off `BrokerCode`"* but the reason that rule can hold: the physical code has
     * to be findable *once*, at claim time, so that everything else — memberships, grants, views,
     * schedules, subscriptions, activity, support references — can key off the logical `GateId`
     * and survive a controller swap. Its own partition, so it is a `GetItem`/conditional `PUT`
     * rather than an index read.
     *
     * **Its `attribute_not_exists(PK)` is the duplicate-install refusal** (D45), and it is a
     * different outcome from a `GateId` collision: this one means *this controller is already
     * installed*, that one means *draw again*. A controller swap moves this mapping and preserves
     * the `GateId`.
     */
    brokerClaim: (code) => ({ PK: `BROKER#${code}`, SK: 'CLAIM' }),
    member: (id, u) => ({ PK: `GATE#${id}`, SK: `MEMBER#${u}` }),
    /**
     * §7.7 / D26 — **the grant pre-registered against a phone number that has no account yet.**
     *
     * A `pending` grant cannot be a `MEMBER#<userId>` row, because the whole point of D26 is
     * that there is no `userId` to key it by: the invitee has not signed up. §9.2 nonetheless
     * requires that *"racing imports dedupe to one pending grant"* through
     * `attribute_not_exists(SK)` — which only holds if the SK is derived from the one identifier
     * the invite does carry. Hence the phone.
     *
     * It lives in the **gate** partition so the roster read reaches it with a `begins_with`
     * rather than a second lookup, and it is GSI1-projected under `PHONE#<e164>` (below) so the
     * D26 attach hook resolves *every* gate a new number was invited to in one query.
     *
     * The row is deleted at attach — the durable record becomes the `MEMBER#<userId>` row — and
     * on D21's revoke-before-accept. **It is never deleted on expiry** (D62).
     *
     * Added by P5, the first PRD with a grant that precedes its user; **promotion to
     * `contracts.md` §9 requested, not granted** (`implementation-notes.md` → P5).
     */
    pendingGrant: (id, phone) => ({
        PK: `GATE#${id}`,
        SK: `PENDING#${phone}`,
    }),
    /**
     * §9 / D58 — a gate's usage log, one row per `AuditEnvelope` (§4).
     *
     * **The SK carries `eventId`, not just `ts` (D127).** A stream retry may redeliver the
     * identical event after a crash between writer acknowledgement and idempotency-row
     * acknowledgement, and "P9 persists and deduplicates by `eventId`" only holds if two events
     * that land in the same millisecond cannot collide on the sort key — `ts` alone is not
     * unique. Appending `eventId` keeps `begins_with(SK, 'LOG#')` and a `ts`-range `BETWEEN`
     * both exactly as they were (a fixed-width ISO instant sorts identically whether or not a
     * suffix follows it), while the write itself becomes `attribute_not_exists(PK)` — the
     * dedup, not a second read.
     *
     * The real writer is P9's (`audit.ts`'s "the rows and their pipeline are P9's"); this
     * builder had no caller before it.
     */
    usageLog: (id, ts, eventId) => ({
        PK: `GATE#${id}`,
        SK: `LOG#${ts}#${eventId}`,
    }),
    /**
     * §4 / D156 — an `AuditEnvelope` with **no `gateId`**: a session, admin, or erasure event.
     * It lives in the **actor's own partition**, sorted by instant, so a `begins_with(SK,
     * 'AUDIT#')` query enumerates every such row a person appears in.
     *
     * **The partition is the erasure access path, and that is the whole reason for the shape.**
     * P10 must pseudonymize `actorUserId` across every row that names a person, and the only
     * other way to find these rows is a table `Scan` — which P1's data policy deliberately
     * withholds and always will. Keyed by `eventId` (as the first cut was) a row is durable,
     * streamable, and **unreachable**: erasure cannot pseudonymize what it cannot enumerate,
     * and the gap does not announce itself, because every other property of the row is fine.
     *
     * The SK carries `ts` before `eventId` for the same reason `usageLog`'s does — a fixed-width
     * ISO instant sorts chronologically, and two events in the same millisecond must not collide.
     *
     * Added by P9 and re-keyed by **D156** before it had ever run against a stage; **promotion to
     * `contracts.md` §9 granted by D156** — see `implementation-notes.md` → P9.
     */
    userAudit: (u, ts, eventId) => ({
        PK: `USER#${u}`,
        SK: `AUDIT#${ts}#${eventId}`,
    }),
    /**
     * The residual: an envelope with **neither `gateId` nor `actorUserId`**. It names no gate to
     * live under and no person to be erased from, and **that is the invariant, not an oversight**
     * — a row this key can claim carries no personal identifier by construction, so the absence
     * of an owner-based access path costs erasure nothing. Anything that would put a `UserId` on
     * such a row belongs on {@link keys.userAudit} instead (D156).
     */
    auditEvent: (eventId) => ({ PK: `AUDIT#${eventId}`, SK: 'EVENT' }),
    /**
     * §9 / **D50 — the erasure tombstone**, and **the only key in this map that names a row in a
     * different table.**
     *
     * That is the whole mechanism, not a detail: a DynamoDB restore replaces the single table and
     * would carry its own tombstones back with it, so a tombstone written *there* cannot testify
     * against the restore that resurrected its subject. The ledger therefore lives outside, with
     * its own PITR and deletion protection (P1's `erasureLedgerTable`), and the replay step of the
     * restore runbook reads it before any traffic reaches the restored table.
     *
     * `ERASURE#<userId>` rather than a hash of one, because the partition has to be reachable from
     * a `userId` a replay found in a restored table — a ledger nobody can look a subject up in
     * cannot gate a restore. The **pseudonymity is in the row, not in the key**: `phoneHash` is an
     * HMAC under the KMS-held pepper (`erasure-pepper.ts`, contracts.md §8) and the raw phone
     * never appears, so the tombstone can outlive every record it describes.
     *
     * The SK carries the instant so the partition is a history rather than a single row: a
     * `Query` returns the erasure and, should a scope narrower than `account` ever exist, the
     * erasures before it. Stage 1 writes exactly one.
     *
     * Added by P10, the ledger's only writer; **promotion to `contracts.md` §9 requested, not
     * granted** (`implementation-notes.md` → P10).
     */
    erasureTombstone: (u, erasedAt) => ({
        PK: `ERASURE#${u}`,
        SK: `TS#${erasedAt}`,
    }),
    /**
     * §9.2 / D58 — the client-generated idempotency key on `POST /gates/{id}/open`.
     *
     * Under the **gate** partition rather than the user's: the thing being made
     * idempotent is *one gate command*, and two members double-tapping the same favourite
     * within a second are two logical requests with two keys, not one. Item TTL reaps the row
     * once no client could still be retrying.
     *
     * §9.2 names the record ("replay of the same key returns the first stored result") and
     * `contracts.md` §9's code block transcribed no key for it. Added by P4 rather than
     * hand-built at the call site, because CI check 7 is the reason the map exists
     * (`implementation-notes.md` → P4); **promoted to `contracts.md` §9 by D116.**
     */
    openIdempotency: (id, key) => ({
        PK: `GATE#${id}`,
        SK: `OPEN#${key}`,
    }),
    /**
     * §9.2 / PEC-12 / D25 — **the offline-BLE receipt claim**, and the row that makes "uploads
     * exactly once" true rather than approximately true.
     *
     * §9.2 **used to** state the primitive as *"the client-generated reconciliation ID is the log
     * item's SK; a duplicate upload is a conditional-write no-op returning the stored result."* That
     * was written when a usage row's SK was the instant alone. **D127 and D156 had since made it
     * `LOG#<ts>#<eventId>`**, so the reconciliation id no longer determined the key on its own —
     * and a duplicate upload carrying the *same* id with a *different* physical timestamp would
     * land on a different sort key and write a **second Activity row for one physical opening**,
     * which is the exact outcome PEC-12 forbids. The conditional write would succeed, and nothing
     * would look wrong. **D171 corrected §9.2's wording to name this row instead**, so the parent
     * and the code now describe the same key.
     *
     * So the id gets a row of its own. It is written under `attribute_not_exists(PK)` **before**
     * the log row, and it carries the timestamp that was accepted — so the loser of the race, and
     * every later retry, reads back the first upload's instant and returns it rather than minting
     * a second row under its own. **The dedupe is still the conditional write, never a prior
     * read** (§9.2's own idiom, and the reason `openIdempotency` beside it has the same shape).
     *
     * **Deliberately no TTL, unlike `openIdempotency`.** That row only has to outlive a client
     * still retrying one request; this one has to outlive a *phone that has been offline*, which
     * is unbounded by construction — the receipt is retried until acknowledged and the device may
     * not reconnect for weeks. An expired claim row would silently restore the duplicate it exists
     * to prevent. One small row per offline BLE open is the cost, and an offline BLE open is a
     * rare event by design.
     *
     * Added by P16, the only writer; **promoted to `contracts.md` §9 by D171**, which corrected
     * §9.2 and named the upload endpoint in the same change. D171 granted this builder **alone** —
     * P5's, P10's and P12's queued promotions are still *requested, not granted*.
     */
    receiptClaim: (id, reconciliationId) => ({
        PK: `GATE#${id}`,
        SK: `RECEIPT#${reconciliationId}`,
    }),
    userMeta: (u) => ({ PK: `USER#${u}`, SK: 'META' }),
    userView: (u, g) => ({ PK: `USER#${u}`, SK: `VIEW#${g}` }),
    userInstall: (u, ts, g) => ({
        PK: `USER#${u}`,
        SK: `INSTALL#${ts}#${g}`,
    }),
    /**
     * D47 / parent §9 **Device session** — at most `MAX_SESSIONS` live rows per user.
     *
     * The parent's §9 table carries this row; contracts.md §9's code block transcribed the
     * other eight and not this one. Added by P2 rather than hand-built at the call site,
     * because CI check 7 is the reason the map exists (`implementation-notes.md` → P2).
     */
    userSession: (u, s) => ({
        PK: `USER#${u}`,
        SK: `SESSION#${s}`,
    }),
    /**
     * Phone → user pointer. **Phone (E.164) is the durable identity anchor** (D49): Cognito
     * cannot be backed up, so `cognitoSub` is an attribute that a pool loss rewrites while
     * this pointer and the `USER#id` subtree stay exactly where they are.
     *
     * Written under `attribute_not_exists(PK)` so two concurrent first sign-ins for one
     * number cannot mint two users — a GSI read would be eventually consistent and would not
     * hold that line. Recycled numbers reach a live identity by design and are **not**
     * mitigated here (D64).
     */
    userByPhone: (phone) => ({ PK: `PHONE#${phone}`, SK: 'USER' }),
    /**
     * D65/D88 — the per-number OTP send window, rolling and with no midnight boundary.
     *
     * Keyed by number rather than by user because the cap must hold for a phone that has
     * never signed up, which is the enumeration and SMS-pumping case it exists for. Item TTL
     * reaps it once the window has passed.
     */
    otpSendWindow: (phone) => ({ PK: `OTP#${phone}`, SK: 'WINDOW' }),
    dealerMeta: (d) => ({ PK: `DEALER#${d}`, SK: 'META' }),
    dealerLedger: (d, ts) => ({
        PK: `DEALER#${d}`,
        SK: `LEDGER#${ts}`,
    }),
    /**
     * P12 — Stage 1's one admin account (`monitor-prd.md` §7.1: `email, cognitoSub, permissionSet,
     * status, createdBy/bootstrapRef, mfaEnrolledAt`). Keyed by the **admin pool's** Cognito `sub`,
     * a plain string — never `UserId`, which brands an identity in the *customer* pool (M2/M6's
     * pools never merge, and typing an admin as a `UserId` would be that merge in the type system).
     *
     * **Deliberately `ADMINACCOUNT#`, not `ADMIN#`.** D156 settled that an audit row carries no
     * `ADMIN#` partition; this is a different entity (the admin's own identity record, not an audit
     * row) and reusing the exact banned prefix for it would make every future grep for "no `ADMIN#`
     * partition" ambiguous about which entity it was checking. `ADMINACCOUNT#` cannot collide with
     * a future `ADMIN#` row and cannot be mistaken for the audit partition D156 forbids.
     *
     * Added by P12, before any row was ever written; **promotion to `contracts.md` §9 requested,
     * not granted** (`implementation-notes.md` → P12).
     */
    adminAccount: (cognitoSub) => ({
        PK: `ADMINACCOUNT#${cognitoSub}`,
        SK: 'PROFILE',
    }),
    /**
     * P12 — the rich detail a Monitor mutation's audit trail needs and `AuditEnvelope` has no
     * field for: M32's old/new `renewsAt` and reason, M25's export field-set/filter/row-count,
     * M23's before/after on a version-checked edit. `writeAuditEvent()` still writes the generic,
     * frozen envelope for the tamper-evident Firehose mirror (`action: 'admin-mutate'`, `via:
     * 'admin'`) — this is a **second, P12-owned item paired with it by the same `eventId`**, not a
     * second writer of the envelope itself (CI check 4 only governs `AuditEnvelope` construction).
     *
     * **The partition is the person the row is *about*, not the admin who acted** — the target when
     * the mutation names one, the acting admin otherwise. This is D156's own rule applied to the one
     * row shape that names *two* people: *"P10 must be able to enumerate every row naming a person."*
     *
     * **The first cut keyed it under the admin, and that was the defect.** A revoke's detail row
     * names the affected resident as `targetUserId` while sitting in `USER#<adminUserId>`, so P10's
     * hot sweep — which queries the **subject's** partition — could not reach it, and the resident's
     * id survived their own erasure. The prefix was never the exposure (`ADMINACCOUNT#` satisfies
     * every reading of D156); the **partition** was. Keying by subject makes the row reachable by the
     * one query erasure already performs, which is why no new index, scan, or access path is owed.
     *
     * **The acting admin stays enumerable through the paired envelope**, which `writeAuditEvent()`
     * keys under its own actor (D156) — so moving the detail row loses nothing: *what did this admin
     * do* is answered by the envelope, *what was written about this person* by the detail row, and
     * the two are joined by the shared `eventId`.
     *
     * `subjectUserId` is a `UserId`; for an admin it is the `asUserId(cognitoSub)` alias, minted once
     * at the boundary the same way every other actor id is.
     *
     * Added by P12; **promotion to `contracts.md` §9 requested, not granted**
     * (`implementation-notes.md` → P12).
     */
    adminMutationDetail: (subjectUserId, ts, eventId) => ({
        PK: `USER#${subjectUserId}`,
        SK: `ADMINDETAIL#${ts}#${eventId}`,
    }),
    /**
     * M26 — a Monitor note on a gate. **Append-only by construction**: no field here or in the
     * item it keys ever changes, because there is no update route and the schema carries no
     * `editedAt` (`monitor-prd.md` §7.1). Lives in the gate's own partition, beside its usage log,
     * so a note reads with a `begins_with(SK, skPrefixes.note)` query rather than a second lookup.
     *
     * Added by P12, before any row was ever written; **promotion to `contracts.md` §9 requested,
     * not granted** (`implementation-notes.md` → P12).
     */
    gateNote: (id, ts) => ({ PK: `GATE#${id}`, SK: `NOTE#${ts}` }),
    /** M26 — the same shape as {@link keys.gateNote}, in a user's own partition. */
    userNote: (u, ts) => ({ PK: `USER#${u}`, SK: `NOTE#${ts}` }),
};
/**
 * The SK prefixes a `begins_with` query needs.
 *
 * A range query cannot use a key builder — it needs the *prefix*, not a complete key — and
 * writing `'MEMBER#'` at the call site is exactly what CI check 7 forbids. Without these the
 * only way to list a gate's roster or a user's installs is a hand-built literal, so the
 * prefixes live here with the builders that produce the full keys.
 *
 * Added by P3, which is the first PRD with a range query (`implementation-notes.md` → P3);
 * **promoted to `contracts.md` §9 by D116.**
 */
export const skPrefixes = {
    member: 'MEMBER#',
    /** P5's pre-registered grants (see `keys.pendingGrant`). Same partition as the roster. */
    pendingGrant: 'PENDING#',
    usageLog: 'LOG#',
    /** P9's gate-less audit rows in the actor's own partition (see `keys.userAudit`, D156). */
    userAudit: 'AUDIT#',
    userView: 'VIEW#',
    userInstall: 'INSTALL#',
    userSession: 'SESSION#',
    dealerLedger: 'LEDGER#',
    /**
     * P10's tombstones (see `keys.erasureTombstone`). The prefix is what a **replay** walks: it
     * holds a `userId` from the restored table and asks the ledger whether that person was erased,
     * which is a `Query` on the partition rather than a point read, because the SK is an instant
     * the replay does not know.
     */
    erasureTombstone: 'TS#',
    /** P12's admin-mutation detail rows (see `keys.adminMutationDetail`), in the *subject's* partition. */
    adminMutationDetail: 'ADMINDETAIL#',
    /** M26's append-only notes (see `keys.gateNote`/`keys.userNote`), one prefix in either partition. */
    note: 'NOTE#',
};
/**
 * **GSI1 — the user's gates** (parent §9): `USER#id` → memberships + per-gate views, so Home
 * is one query.
 *
 * P1 provisioned the index and named its attributes; **no builder existed for the values**,
 * which left every writer of a membership or a view row free to invent its own — the precise
 * failure `keys` exists to prevent, one index removed. Added by P3, the first PRD to write a
 * GSI1-projected item; **promoted to `contracts.md` §9 by D116**
 * (`implementation-notes.md` → P3).
 */
export const gsiKeys = {
    userGate: (u, g) => ({ GSI1PK: `USER#${u}`, GSI1SK: `GATE#${g}` }),
    /** The partition half alone — what a `Query` on GSI1 needs, with no gate to name. */
    userGates: (u) => ({ GSI1PK: `USER#${u}` }),
    /**
     * **GSI1, overloaded once more — the pending grants waiting on a phone number** (D26).
     *
     * GSI1 already carries two partitions per user (`USER#id` → memberships + views). This adds
     * a third partition *kind* on the same index rather than a fourth index: at OTP sign-up the
     * attach hook holds a phone and needs every gate that number was invited to, which is one
     * query here and a table scan without it.
     *
     * `PHONE#<e164>` is the same partition shape `keys.userByPhone` uses on the base table, so
     * the number appears in one form across both. Added by P5;
     * **promotion to `contracts.md` §9 requested, not granted.**
     */
    phoneGrant: (phone, g) => ({
        GSI1PK: `PHONE#${phone}`,
        GSI1SK: `GATE#${g}`,
    }),
    /** The partition half alone — the attach hook's one query. */
    phoneGrants: (phone) => ({ GSI1PK: `PHONE#${phone}` }),
    /**
     * **GSI2 — installer history and Dealer association** (parent §9). The install rows are
     * already keyed under `USER#id` on the base table, so Stage 1 reads them there; the
     * projection exists for the Dealer-side reads P13 adds.
     */
    userInstall: (u, ts, g) => ({
        GSI2PK: `USER#${u}`,
        GSI2SK: `INSTALL#${ts}#${g}`,
    }),
};
/*
 * ---------------------------------------------------------------------------
 * Brand constructors — the only `as <Brand>` expressions in the package.
 *
 * They are here rather than in `ids.ts` so that CI check 6's grep ("either brand cast away
 * outside the key-builder module") has exactly one legitimate file to allow.
 * ---------------------------------------------------------------------------
 */
/**
 * The logical gate's permanent primary key — **eight decimal digits**, `^[0-9]{8}$`
 * (contracts.md §1). Server-generated; see `gate-id.ts` for the generator.
 *
 * **Exact, and deliberately not forgiving.** It does not trim, upper-case, strip separators,
 * or map look-alikes, because "normalization happens once, at the edge; storage and comparison
 * are exact" is only true if the brand constructor is the strict end of that pair.
 * `normalizeGateIdInput` (`gate-id.ts`) is the repairing edge, and it calls this.
 *
 * Validated here because it is a partition key: a mistyped code silently creates a second,
 * empty gate rather than failing. **The leading zero is part of the value** — anything that
 * reaches this having passed through `Number()` arrives seven digits long and is refused,
 * which is the intended way to find that bug.
 */
export function asGateId(raw) {
    if (!/^[0-9]{8}$/.test(raw)) {
        throw new TypeError('GateId must be exactly eight digits');
    }
    return raw;
}
/**
 * The transport address of whatever controller currently answers for a gate — the **printed
 * six-character alphanumeric** code from the QR sticker.
 *
 * **Not derived from the `GateId` and not validated against it** (contracts.md §1). It is
 * carried by the installation claim, mapped to a generated `GateId` by P3's broker claim, and
 * rewritten — alone — by a controller swap.
 *
 * **Case is significant and this function must not normalize it (D131).** MQTT topic names are
 * case-sensitive, and `S-<code>`/`C-<code>` embed the code verbatim — so `09F22C` and `09f22c`
 * address **different topics** on the vendor's broker. This function used to `.toUpperCase()`,
 * on the reasonable-sounding theory that a technician may type a sticker code in either case.
 * That was wrong in the worst available way: an installation with a lower-case sticker stored
 * the upper-cased code, provisioned cleanly, published into a topic no device listened on, and
 * reported the gate **offline forever** — with no error anywhere, because publishing to an
 * unsubscribed topic is not a failure. It cost a live `dev` gate on 2026-08-10.
 *
 * Trimming stays: surrounding whitespace is a paste artefact and addresses nothing. A `GateId`
 * gets neither, and that asymmetry is still deliberate.
 *
 * **The cost of this correctness is real and accepted:** two codes differing only in case are
 * now two different `BrokerCode`s, so a mistyped case no longer silently "works" — it fails to
 * claim, or claims a second gate for one controller. A wrong-case code that fails loudly at
 * install beats one that succeeds and never opens the gate.
 */
export function asBrokerCode(raw) {
    const value = raw.trim();
    if (!/^[A-Za-z0-9]{6}$/.test(value)) {
        throw new TypeError('BrokerCode must be six alphanumeric characters');
    }
    return value;
}
export function asUserId(raw) {
    if (raw.length === 0)
        throw new TypeError('UserId must not be empty');
    return raw;
}
export function asSessionId(raw) {
    if (raw.length === 0)
        throw new TypeError('SessionId must not be empty');
    return raw;
}
export function asDealerId(raw) {
    if (raw.length === 0)
        throw new TypeError('DealerId must not be empty');
    return raw;
}
/**
 * Canonical UTC ISO-8601 with milliseconds. Rejects offset-bearing and second-precision
 * strings, because sort-key ordering is only lexicographic if the format is fixed.
 */
export function asIsoInstant(raw) {
    const value = raw instanceof Date ? raw.toISOString() : raw;
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) {
        throw new TypeError('IsoInstant must be canonical UTC ISO-8601 with milliseconds');
    }
    if (Number.isNaN(Date.parse(value))) {
        throw new TypeError('IsoInstant must be a real instant');
    }
    return value;
}
/**
 * Applies the E.164 brand to an already-canonical string. **Normalization is
 * `normalizePhone` (contracts.md §11)** — this is the brand application it uses, kept here
 * with the other constructors so the package has one casting site.
 */
export function asE164(raw) {
    if (!/^\+[1-9]\d{7,14}$/.test(raw)) {
        throw new TypeError('E164 must be a canonical +<country><subscriber> string');
    }
    return raw;
}
//# sourceMappingURL=keys.js.map